import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Save, X, Search, RefreshCw, Package
} from 'lucide-react';
import ImageUploader from '@/components/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PART_CATEGORIES = [
  'Pre-Filters', 'Main Filters', 'Post-Filters', 'UV Components',
  'Accessories', 'Service Kits', 'Filter Combos', 'Pumps & Motors', 'Other'
];

const BRANDS = ['Kent', 'Aquaguard', 'Livpure', 'Blue Star', 'Pureit', 'Universal'];

function AdminSpareParts() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchParts(); }, []);

  const fetchParts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/spare-parts`);
      if (res.ok) setParts(await res.json());
    } catch { toast.error('Failed to load spare parts'); }
    finally { setLoading(false); }
  };

  const savePart = async (data) => {
    setSaving(true);
    try {
      const isNew = !data.id;
      const url = isNew ? `${BACKEND_URL}/api/spare-parts` : `${BACKEND_URL}/api/spare-parts/${data.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        toast.success(isNew ? 'Spare part created!' : 'Spare part updated!');
        fetchParts();
        setEditing(null);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } catch { toast.error('Save error'); }
    finally { setSaving(false); }
  };

  const deletePart = async (id) => {
    if (!confirm('Delete this spare part permanently?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/spare-parts/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Spare part deleted'); fetchParts(); }
    } catch { toast.error('Delete failed'); }
  };

  const newPart = () => setEditing({
    name: '', category: 'Pre-Filters', compatibility: [],
    price: 0, originalPrice: 0, image: '', description: '',
    lifespan: '', inStock: true
  });

  const filtered = parts.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen" data-testid="admin-spare-parts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="spare-parts-title">Spare Parts Management</h1>
          <p className="text-gray-500 text-sm">Manage RO spare parts and accessories</p>
        </div>
        <button onClick={newPart} data-testid="add-spare-part-btn"
          className="flex items-center gap-2 px-4 py-2 bg-lime-600 text-white rounded-lg hover:bg-lime-700 text-sm font-medium">
          <Plus size={16} /> Add Spare Part
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm mb-4 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input type="text" placeholder="Search spare parts..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-lime-500 focus:border-lime-500"
            data-testid="spare-parts-search" />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-lime-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="spare-parts-empty">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-medium text-gray-900 mb-1">No spare parts yet</h3>
            <p className="text-gray-500 text-sm mb-4">Add your first spare part to get started</p>
            <button onClick={newPart} className="px-4 py-2 bg-lime-600 text-white rounded-lg hover:bg-lime-700 text-sm">
              <Plus size={16} className="inline mr-1" /> Add Spare Part
            </button>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="spare-parts-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Part</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Price</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Stock</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(part => (
                <tr key={part.id} className="hover:bg-gray-50" data-testid={`spare-part-row-${part.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {part.image && <img src={part.image} alt="" className="w-10 h-10 object-cover rounded border" />}
                      <div>
                        <div className="font-medium text-gray-900">{part.name}</div>
                        <div className="text-xs text-gray-500">{part.lifespan}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-lime-100 text-lime-700 text-xs font-medium rounded">{part.category}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">{'\u20B9'}{part.price}</div>
                    {part.originalPrice > part.price && (
                      <div className="text-xs text-gray-400 line-through">{'\u20B9'}{part.originalPrice}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${part.inStock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {part.inStock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => setEditing({ ...part })}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                        data-testid={`edit-part-${part.id}`}>
                        <Edit size={14} className="inline mr-1" /> Edit
                      </button>
                      <button onClick={() => deletePart(part.id)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                        data-testid={`delete-part-${part.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <SparePartEditor part={editing} onSave={savePart} onClose={() => setEditing(null)} saving={saving} />
      )}
    </div>
  );
}

function SparePartEditor({ part, onSave, onClose, saving }) {
  const [fd, setFd] = useState({ ...part, compatibility: part.compatibility || [] });

  const set = (field, value) => setFd(prev => ({ ...prev, [field]: value }));

  const toggleBrand = (brand) => {
    const current = fd.compatibility || [];
    set('compatibility', current.includes(brand) ? current.filter(b => b !== brand) : [...current, brand]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fd.name?.trim()) { toast.error('Name is required'); return; }
    if (!fd.price || fd.price <= 0) { toast.error('Valid price is required'); return; }
    onSave(fd);
  };

  const inputCls = "w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lime-500 focus:border-lime-500 text-sm";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="spare-part-editor-modal">
      <div className="bg-white rounded-xl my-6 shadow-2xl max-w-2xl w-full">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10 rounded-t-xl">
          <span className="text-sm font-semibold text-gray-800">{fd.id ? 'Edit Spare Part' : 'New Spare Part'}</span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm" data-testid="part-editor-cancel">Cancel</button>
            <button onClick={handleSubmit} disabled={saving} data-testid="part-editor-save"
              className="px-4 py-1.5 bg-lime-600 text-white rounded-lg hover:bg-lime-700 disabled:opacity-50 text-sm font-medium flex items-center gap-1.5">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 56px)' }}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Name *</label>
            <input type="text" value={fd.name || ''} onChange={e => set('name', e.target.value)}
              className={inputCls} placeholder="e.g. Sediment Filter (Spun)" data-testid="part-name" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Category *</label>
              <select value={fd.category || ''} onChange={e => set('category', e.target.value)} className={inputCls} data-testid="part-category">
                {PART_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Lifespan</label>
              <input type="text" value={fd.lifespan || ''} onChange={e => set('lifespan', e.target.value)}
                className={inputCls} placeholder="e.g. 6-12 months" data-testid="part-lifespan" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Price ({'\u20B9'}) *</label>
              <input type="number" value={fd.price || ''} onChange={e => set('price', parseFloat(e.target.value) || 0)}
                className={inputCls} placeholder="299" data-testid="part-price" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Original Price ({'\u20B9'})</label>
              <input type="number" value={fd.originalPrice || ''} onChange={e => set('originalPrice', parseFloat(e.target.value) || 0)}
                className={inputCls} placeholder="399" data-testid="part-original-price" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description *</label>
            <textarea value={fd.description || ''} onChange={e => set('description', e.target.value)}
              className={`${inputCls} resize-y`} rows={3} placeholder="Part description" data-testid="part-description" />
          </div>

          <ImageUploader value={fd.image || ''} onChange={url => set('image', url)} folder="products" type="product" label="Part Image" />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Compatible Brands</label>
            <div className="flex flex-wrap gap-2">
              {BRANDS.map(brand => (
                <button key={brand} type="button" onClick={() => toggleBrand(brand)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    (fd.compatibility || []).includes(brand)
                      ? 'bg-lime-600 text-white border-lime-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-lime-400'
                  }`} data-testid={`brand-toggle-${brand}`}>
                  {brand}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-6 pt-2 border-t">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" id="part_stock" checked={fd.inStock !== false} onChange={e => set('inStock', e.target.checked)}
                className="w-4 h-4 text-lime-600 rounded" data-testid="part-in-stock" />
              <span className="text-sm text-gray-600">In Stock</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" id="part_homepage" checked={fd.show_on_homepage || false} onChange={e => set('show_on_homepage', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded" data-testid="part-show-on-homepage" />
              <span className="text-sm text-gray-600">Show on Homepage</span>
            </label>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminSpareParts;
